/**
 * 画布管理页：列出所有画布，支持进入编辑 / 重命名 / 删除
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { canvasApi, type CanvasSummary } from '../utils/canvas-api'
import { useConfirmStore, ConfirmDialog } from '../components/canvas/ConfirmDialog'

interface CanvasListItem extends CanvasSummary {
  nodeCount?: number
}

export function CanvasListPage() {
  const navigate = useNavigate()
  const [items, setItems] = useState<CanvasListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  const refresh = async () => {
    setLoading(true)
    setError(null)
    try {
      const list = await canvasApi.list()
      setItems(list.items)
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void refresh()
  }, [])

  const handleOpen = (id: string) => {
    navigate(`/canvas?id=${encodeURIComponent(id)}`)
  }

  const handleCreate = async () => {
    setCreating(true)
    try {
      const created = await canvasApi.create('Untitled Canvas', {})
      navigate(`/canvas?id=${encodeURIComponent(created.id)}`)
    } catch (e) {
      setError(e instanceof Error ? e.message : '创建失败')
    } finally {
      setCreating(false)
    }
  }

  const handleRename = async (item: CanvasListItem) => {
    const next = window.prompt('画布名称', item.name)
    if (!next || !next.trim() || next === item.name) return
    try {
      await canvasApi.update(item.id, { name: next.trim() })
      await refresh()
    } catch (e) {
      console.error('rename failed', e)
    }
  }

  const handleDelete = (item: CanvasListItem) => {
    useConfirmStore.getState().show({
      title: '删除画布',
      message: `确认删除画布"${item.name}"？删除后无法恢复。`,
      confirmText: '删除',
      danger: true,
      onConfirm: async () => {
        try {
          await canvasApi.remove(item.id)
          await refresh()
        } catch (e) {
          console.error('delete failed', e)
        }
      },
    })
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: '#020617',
        color: '#e2e8f0',
        fontFamily: 'Inter, system-ui, sans-serif',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <header
        style={{
          flex: '0 0 56px',
          display: 'flex',
          alignItems: 'center',
          padding: '0 24px',
          gap: 16,
          background: '#0b1120',
          borderBottom: '1px solid #1e293b',
        }}
      >
        <a href="/" style={{ color: '#94a3b8', textDecoration: 'none', fontSize: 13 }}>
          ← 返回
        </a>
        <span
          style={{
            background: 'linear-gradient(90deg,#6366f1,#8b5cf6,#06b6d4)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 700,
            letterSpacing: 0.5,
            fontSize: 16,
          }}
        >
          我的画布
        </span>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          onClick={handleCreate}
          disabled={creating}
          style={{
            background: 'linear-gradient(90deg,#6366f1,#8b5cf6)',
            border: 'none',
            color: '#fff',
            padding: '8px 16px',
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 600,
            cursor: creating ? 'not-allowed' : 'pointer',
            opacity: creating ? 0.5 : 1,
          }}
        >
          + 新建画布
        </button>
      </header>

      <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
        {loading ? (
          <div style={{ color: '#94a3b8', fontSize: 14 }}>加载中…</div>
        ) : error ? (
          <div style={{ color: '#f87171', fontSize: 14 }}>加载失败：{error}</div>
        ) : items.length === 0 ? (
          <div style={{ color: '#475569', fontSize: 14, textAlign: 'center', marginTop: 80 }}>
            还没有画布，点击右上角"+ 新建画布"开始
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 16,
            }}
          >
            {items.map((item) => (
              <div
                key={item.id}
                style={{
                  background: '#0f172a',
                  border: '1px solid #1e293b',
                  borderRadius: 12,
                  padding: 16,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  transition: 'border-color 0.15s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#475569')}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#1e293b')}
              >
                <div
                  onClick={() => handleOpen(item.id)}
                  style={{
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: 14,
                    color: '#f1f5f9',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={item.name}
                >
                  {item.name || 'Untitled'}
                </div>
                <div style={{ fontSize: 11, color: '#64748b' }}>
                  更新于 {formatDate(item.updated_at)}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button
                    type="button"
                    onClick={() => handleOpen(item.id)}
                    style={{
                      flex: 1,
                      background: 'linear-gradient(90deg,#6366f1,#8b5cf6)',
                      border: 'none',
                      color: '#fff',
                      padding: '6px 10px',
                      borderRadius: 6,
                      fontSize: 12,
                      cursor: 'pointer',
                    }}
                  >
                    打开
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRename(item)}
                    style={{
                      background: 'transparent',
                      border: '1px solid #334155',
                      color: '#cbd5e1',
                      padding: '6px 10px',
                      borderRadius: 6,
                      fontSize: 12,
                      cursor: 'pointer',
                    }}
                  >
                    重命名
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(item)}
                    style={{
                      background: 'transparent',
                      border: '1px solid #7f1d1d',
                      color: '#f87171',
                      padding: '6px 10px',
                      borderRadius: 6,
                      fontSize: 12,
                      cursor: 'pointer',
                    }}
                  >
                    删除
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog />
    </div>
  )
}

function formatDate(ts: number): string {
  const d = new Date(ts)
  return d.toLocaleString([], {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}
